import { AfterViewInit, Component, ElementRef, HostListener, Input, OnDestroy, ViewChild } from '@angular/core';
import * as THREE from 'three';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls';

@Component({
  selector: 'dd-three-sixty-photo-viewer',
  templateUrl: './three-sixty-photo-viewer.component.html',
  styleUrls: ['./three-sixty-photo-viewer.component.scss']
})
export class ThreeSixtyPhotoViewerComponent implements AfterViewInit, OnDestroy {
  @Input() public imageUrl: string;
  @Input() public hidden: boolean;
  @ViewChild('photoViewer', { static: false }) viewer: ElementRef;
  @ViewChild('loader', { static: false }) loader: ElementRef;

  public isWebGLAvailable = true;
  private readonly scene: THREE.Scene = new THREE.Scene();
  private readonly camera = new THREE.PerspectiveCamera(75, window.innerWidth / window.innerHeight, 1, 1000);
  private renderer: THREE.WebGLRenderer;
  private readonly loadingManager = new THREE.LoadingManager();
  private orbitControls: OrbitControls;
  private animationFrameId: number;
  private viewerTimeoutId: number;

  @HostListener('window:resize', ['$event'])
  onWindowResize() {
    this.setRendererSize();
  }

  constructor() {
    this.checkWebGLAvailability();
  }

  ngAfterViewInit(): void {
    this.viewerTimeoutId = setTimeout(() => {
      if (this.isWebGLAvailable) {
        this.init360Viewer();
        this.handleWindowResizeEvent();
      }
    });
  }

  ngOnDestroy(): void {
    cancelAnimationFrame(this.animationFrameId);
    clearTimeout(this.viewerTimeoutId);
  }

  private setRendererSize(): void {
    const width = window.innerWidth;

    if (width >= 1130) {
      this.renderer.setSize(766, 431);
    }

    if (width >= 599 && width < 1130) {
      this.renderer.setSize(551, 310);
    }

    if (width < 599) {
      this.renderer.setSize(321, 180);
    }
  }

  private checkWebGLAvailability(): void {
    try {
      this.renderer = new THREE.WebGLRenderer({
        antialias: true,
        alpha: true,
        precision: "lowp",
        powerPreference: "low-power"
      });
      this.isWebGLAvailable = true;
    } catch (error) {
      this.isWebGLAvailable = false;
    }
  }

  private init360Viewer(): void {
    // Place the renderer in the template
    this.setRendererSize();
    this.viewer.nativeElement.appendChild(this.renderer.domElement);

    // Set camera position
    this.camera.position.set(10, 2, 1);

    this.configureLoadingManagerEvents();
    this.configureOrbitControls();
    this.create360PhotoSphere();
    this.startAnimation();
  }

  private create360PhotoSphere() {
    // Create a 360-degree sphere with a photo texture
    const textureLoader = new THREE.TextureLoader(this.loadingManager);
    const texture = textureLoader.load(this.imageUrl);
    texture.mapping = THREE.EquirectangularReflectionMapping;

    // Adding sphere to place the image
    const sphereGeometry = new THREE.SphereGeometry(500, 60, 60);
    sphereGeometry.scale(-1, 1, 1);

    // Map texture image to sphere
    const sphereMaterial = new THREE.MeshBasicMaterial({map: texture});
    const sphere = new THREE.Mesh(sphereGeometry, sphereMaterial);
    this.scene.add(sphere);
  }

  private configureOrbitControls() {
    // Add orbit controls, to allow moving the photo with the mouse
    this.orbitControls = new OrbitControls(this.camera, this.renderer.domElement);
    this.orbitControls.enableZoom = true;
    this.orbitControls.enablePan = true;
    this.orbitControls.minDistance = 100;
    this.orbitControls.maxDistance = 800;
    this.orbitControls.enableDamping = true;
    this.orbitControls.dampingFactor = 0.05;
  }

  private configureLoadingManagerEvents() {
    // Loading Manager Events
    this.loadingManager.onLoad = () => {
      this.loader.nativeElement.style.display = 'none';
    };

    this.loadingManager.onError = (url) => {
      console.error('There was an error loading ' + url);
      this.loader.nativeElement.style.display = 'none';
    };
  }

  private startAnimation() {
    let lastUpdate = 0;
    const animate = () => {
      // Animation loop
      this.animationFrameId = requestAnimationFrame(animate);
      if (performance.now() - lastUpdate > 16) {
        this.orbitControls.update();
        lastUpdate = performance.now();
      }
      this.camera.aspect = window.innerWidth / window.innerHeight;
      this.renderer.render(this.scene, this.camera);
    };

    animate();
  }

  private handleWindowResizeEvent() {
    // Handle window resize event
    window.addEventListener('resize', () => {
      const newWidth = window.innerWidth;
      const newHeight = window.innerHeight * 0.6;

      this.camera.aspect = newWidth / newHeight;
      this.camera.updateProjectionMatrix();

      this.renderer.setSize(newWidth, newHeight);
    });
  }
}
